import { makeAutoObservable } from 'mobx';
import { iframeCommunicationStore } from './communication/IframeCommunicationStore';
import { logger } from '@/utils/logger';
import { cacheManager } from '../utils/cacheManager';
import { messageStore } from './MessageStore';
import { userStore } from './UserStore';
import { accountStore } from './AccountStore';
import { getVirtualServiceCredentials, getServiceStatus } from '../apis/loginApi';
import { loginStateManager } from './communication/LoginStateManager';

class ChatStore {
  // 聊天状态
  loading = false;
  error = null;
  // 会话信息
  conversationId = null; // 服务端会话ID
  hasConversationStarted = true;
  // 创建者信息
  creator = null;
  // 服务状态结果
  serviceStatusResult = null;

  // 防抖定时器
  _joinChatTimer = null;
  _lastJoinChatUserId = null;

  constructor() {
    makeAutoObservable(this);
  }

  setLoading(status) {
    this.loading = status;
  }

  setError(error) {
    this.error = error;
    // 使用MessageStore显示错误信息给用户
    if (error) {
      messageStore.error(error);
    }
  }

  setCreator(creator) {
    this.creator = creator;
  }

  setConversationId(conversationId) {
    this.conversationId = conversationId;
    if (conversationId) {
      this.setHasConversationStarted(true);
    }
  }

  setHasConversationStarted(status) {
    this.hasConversationStarted = status;
  }

  setServiceStatusResult(result) {
    this.serviceStatusResult = result;
  }

  async joinChat(userInfo, chatUserId) {
    // 清除之前的防抖定时器
    if (this._joinChatTimer) {
      clearTimeout(this._joinChatTimer);
    }

    // 如果用户ID没有变化，跳过重复操作
    if (chatUserId === this._lastJoinChatUserId && this._lastJoinChatUserId !== null) {
      logger.debug('[ChatStore] 用户ID未变化，跳过重复的joinChat操作', {
        chatUserId,
      });
      return;
    }

    // 立即更新最后处理的用户ID，避免重复检查
    this._lastJoinChatUserId = chatUserId;

    // 设置防抖定时器
    this._joinChatTimer = setTimeout(async () => {
      // 再次检查用户ID是否变化
      if (chatUserId !== this._lastJoinChatUserId && this._lastJoinChatUserId !== null) {
        logger.debug('[ChatStore] 用户ID已变化，跳过joinChat操作', {
          originalUserId: chatUserId,
          currentUserId: this._lastJoinChatUserId,
        });
        return;
      }

      this.setLoading(true);
      this.setError(null);
      this.setHasConversationStarted(true);

      try {
        // 1. 检查是否有缓存的虚拟客服登录信息
        const cachedLoginInfo = cacheManager.getVirtualServiceLoginInfo(userInfo.tenantId);
        let serviceChatUserId = null;

        if (cachedLoginInfo) {
          serviceChatUserId = cachedLoginInfo.virtualServiceUserId;
          logger.info('[ChatStore] 使用缓存的虚拟客服ID:', { serviceChatUserId });
        }

        // 2. 调用服务状态接口
        const serviceStatusParams = {
          chatUserId,
        };

        // 如果有缓存的虚拟客服ID，则传递
        if (serviceChatUserId) {
          serviceStatusParams.loginVirtualServiceId = serviceChatUserId;
        }

        const serviceStatusResult = await getServiceStatus(serviceStatusParams);

        const { conversation, virtualServiceUser, buyerUser } = serviceStatusResult;

        // 检查必要字段是否存在（包括null值）
        if (!conversation || !virtualServiceUser || !buyerUser) {
          const errorMessage = '服务状态响应数据不完整';
          this.setError(errorMessage);
          logger.error('[ChatStore] 服务状态响应数据不完整:', {
            hasConversation: !!conversation,
            hasVirtualServiceUser: !!virtualServiceUser,
            hasBuyerUser: !!buyerUser,
            responseData: serviceStatusResult,
          });

          // 清空相关状态
          this.setConversationId(null);
          this.setCreator(null);
          this.setServiceStatusResult(null);

          return;
        }

        // 3. 缓存虚拟客服登录信息
        cacheManager.setVirtualServiceLoginInfo(userInfo.tenantId, {
          virtualServiceUserId: virtualServiceUser.id,
          conversationId: conversation.id,
          timestamp: Date.now(),
        });

        logger.info('[ChatStore] 检测到需要重新登录，将通过 SWITCH_CONVERSATION 传递新凭证');

        // 更新会话信息，不在这里获取登录凭证，让LoginStateManager统一处理
        this.setServiceStatusResult({
          conversation: {
            id: conversation.id,
            groupName: conversation.groupName,
            memberCount: conversation.memberCount,
            ...conversation,
          },
          virtualServiceUser: {
            id: virtualServiceUser.id,
            ...virtualServiceUser,
          },
          buyerUser: {
            id: buyerUser.id,
            ...buyerUser,
          },
        });

        // 调用登录流程管理器设置凭证，避免重复调用API
        await loginStateManager.handleLoginFlow({
          chatUserId,
          tenantId: userInfo.tenantId,
          existingStatusResult: {
            conversation: {
              id: conversation.id,
            },
            virtualServiceUser: {
              id: virtualServiceUser.id,
            },
            buyerUser: {
              id: buyerUser.id,
            },
            needReLogin: false,
          },
        });

        logger.info('[ChatStore] 登录流程完成，凭证已设置到ChatCredentialsStore');

        // 5. 在新的架构中，凭证由子窗体管理，不再在父窗体中存储

        // 6. 更新用户信息到UserStore
        if (buyerUser) {
          userStore._users.set(buyerUser.id.toString(), {
            id: buyerUser.id,
            nickName: buyerUser.nickName,
            serialNumber: buyerUser.serialNumber, // 添加序列号字段
            avatarUrl: buyerUser.avatarUrl,
            currentGroupCount: buyerUser.currentGroupCount,
            conversationId: conversation.id, // 添加会话ID
          });
          logger.debug('[ChatStore] 更新买家用户信息到UserStore:', {
            userId: buyerUser.id,
            nickName: buyerUser.nickName,
            serialNumber: buyerUser.serialNumber, // 添加序列号日志
            hasAvatar: !!buyerUser.avatarUrl,
            conversationId: conversation.id,
          });
        }

        // 7. 设置会话信息 - 确保包含所有字段，包括 serialNumber
        this.setConversationId(conversation.id);
        this.setCreator(buyerUser.id);

        const joinChatServiceStatusResult = {
          conversation: {
            id: conversation.id,
            ...conversation,
          },
          virtualServiceUser: {
            id: virtualServiceUser.id,
            ...virtualServiceUser,
          },
          buyerUser: {
            id: buyerUser.id,
            ...buyerUser,
          },
        };

        this.setServiceStatusResult(joinChatServiceStatusResult);

        // 同时更新 ChatUserPickerStore 的服务状态数据
        const { chatUserPickerStore } = await import('./ChatUserPickerStore');
        chatUserPickerStore.updateServiceStatusData(joinChatServiceStatusResult);

        logger.info('[ChatStore] 获取到会话信息:', {
          conversationId: conversation.id,
          iframeReady: iframeCommunicationStore.isIframeReady,
        });

        // 8. 在 joinChat 阶段不等待 iframe 就绪，只设置状态
        // iframe 的初始化和会话切换将在用户选择时进行
        logger.info('[ChatStore] joinChat 完成，等待用户选择后初始化 iframe');
      } catch (error) {
        // 直接使用错误信息，request.js已经处理了错误传递
        const errorMessage = error.message || '加入聊天失败';
        this.setError(errorMessage);
        this.setConversationId(null);
        this.setCreator(null);
        this.setServiceStatusResult(null); // 清空服务状态结果
        logger.error('[ChatStore] 加入聊天异常:', error);
      } finally {
        this.setLoading(false);
      }
    }, 150); // 150ms 防抖延迟，提高响应速度
  }

  /**
   * 等待 iframe 就绪后执行会话切换
   * 简化实现，不再等待 TUIChatEngine 状态变化
   */
  async _waitForIframeReadyAndLogin(credentials) {
    try {
      logger.info('[ChatStore] 开始执行会话切换流程');

      // 直接使用传入的 credentials 参数，避免从缓存读取过期数据
      // credentials 包含了从 getVirtualServiceCredentials 获取的最新凭证信息
      if (!credentials?.userId) {
        logger.error('[ChatStore] credentials 中缺少 userId');
        throw new Error('credentials 中缺少 userId');
      }

      // 从 credentials 中获取 serviceUserId，这是最新获取的虚拟客服ID
      const serviceUserId = credentials.userId;

      // 优先使用 credentials 中的 conversationId，这是从登录接口返回的正确值
      const conversationId = credentials.conversationId;

      if (!conversationId) {
        logger.error('[ChatStore] credentials 中缺少 conversationId');
        throw new Error('credentials 中缺少 conversationId');
      }

      logger.info('[ChatStore] 发送切换会话消息（使用最新凭证）', {
        conversationId,
        serviceUserId,
        hasCredentials: !!credentials,
        credentialsConversationId: credentials.conversationId,
        credentialsKeys: Object.keys(credentials || {}),
      });

      // 使用最新的凭证信息发送切换会话消息
      await iframeCommunicationStore.sendSwitchConversationMessage(
        document.querySelector('iframe'),
        conversationId,
        serviceUserId,
        credentials
      );

      logger.info('[ChatStore] 会话切换完成');
    } catch (error) {
      logger.error('[ChatStore] 会话切换流程失败:', error);
      throw error;
    }
  }

  /**
   * 直接发送切换消息（iframe 已就绪时使用）
   */
  async _sendSwitchMessageDirectly(credentials) {
    try {
      logger.info('[ChatStore] 开始直接发送切换消息');

      // 直接使用传入的 credentials 参数，避免从缓存读取过期数据
      // credentials 包含了从 getVirtualServiceCredentials 获取的最新凭证信息
      if (!credentials?.userId) {
        logger.error('[ChatStore] credentials 中缺少 userId');
        throw new Error('credentials 中缺少 userId');
      }

      // 从 credentials 中获取 serviceUserId，这是最新获取的虚拟客服ID
      const serviceUserId = credentials.userId;

      // 优先使用 credentials 中的 conversationId，这是从登录接口返回的正确值
      const conversationId = credentials.conversationId;

      if (!conversationId) {
        logger.error('[ChatStore] credentials 中缺少 conversationId');
        throw new Error('credentials 中缺少 conversationId');
      }

      logger.info('[ChatStore] 直接发送切换会话消息（使用最新凭证）', {
        conversationId,
        serviceUserId,
        hasCredentials: !!credentials,
        credentialsConversationId: credentials.conversationId,
        credentialsKeys: Object.keys(credentials || {}),
      });

      // 直接发送切换会话消息，不等待 iframe 就绪
      await iframeCommunicationStore.sendSwitchConversationMessage(
        document.querySelector('iframe'),
        conversationId,
        serviceUserId,
        credentials
      );

      logger.info('[ChatStore] 直接发送切换消息完成');
    } catch (error) {
      logger.error('[ChatStore] 直接发送切换消息失败:', error);
      throw error;
    }
  }

  /**
   * 等待注销成功事件
   */
  async _waitForLogoutSuccess() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('等待注销成功超时'));
      }, 10000); // 10秒超时

      const handleLogoutSuccess = event => {
        if (event.type === 'chat-logout-success') {
          clearTimeout(timeout);
          window.removeEventListener('chat-logout-success', handleLogoutSuccess);
          resolve();
        }
      };

      window.addEventListener('chat-logout-success', handleLogoutSuccess);
    });
  }

  /**
   * 获取虚拟客服登录凭证
   * @param {Object} params - 登录参数
   * @param {string} params.serviceUserId - 服务用户ID
   * @param {string} params.conversationId - 会话ID
   * @returns {Promise<Object>} 登录凭证
   */
  async getVirtualServiceCredentials(params) {
    try {
      logger.info('[ChatStore] 获取虚拟客服登录凭证:', {
        serviceUserId: params.serviceUserId,
        conversationId: params.conversationId,
      });

      const result = await getVirtualServiceCredentials(params);

      logger.info('[ChatStore] 获取登录凭证成功:', {
        sdkAppId: result?.sdkAppId,
        userId: result?.userId,
        hasUserSig: !!result?.userSig,
        conversationId: result?.conversationId,
        resultKeys: Object.keys(result || {}),
      });

      return result;
    } catch (error) {
      logger.error('[ChatStore] 获取登录凭证失败:', error);
      throw new Error(`获取登录凭证失败: ${error.message}`);
    }
  }

  // 获取已有的服务状态结果
  getServiceStatusResult() {
    // 如果有保存的服务状态结果，直接返回
    if (this.serviceStatusResult) {
      logger.info('[ChatStore] 返回保存的服务状态结果:', {
        conversationId: this.serviceStatusResult.conversation?.id,
        needReLogin: this.serviceStatusResult.needReLogin,
      });
      return this.serviceStatusResult;
    }

    // 如果已经有会话信息，说明已经调用过/service/status
    if (this.conversationId && this.creator) {
      logger.info('[ChatStore] 返回已有的服务状态结果:', {
        conversationId: this.conversationId,
        creator: this.creator,
      });

      // 从缓存中获取虚拟客服信息
      const cachedLoginInfo = cacheManager.getVirtualServiceLoginInfo(
        accountStore.getCurrentUser()?.tenantId
      );

      // 检查缓存的虚拟客服ID是否存在
      if (!cachedLoginInfo?.virtualServiceUserId) {
        logger.warn('[ChatStore] 缓存中没有虚拟客服ID，返回null');
        return null;
      }

      return {
        needReLogin: false, // 假设不需要重新登录
        conversation: {
          id: this.conversationId,
        },
        virtualServiceUser: {
          id: cachedLoginInfo.virtualServiceUserId,
        },
        buyerUser: {
          id: this.creator,
        },
      };
    }

    return null;
  }

  reset() {
    this.loading = false;
    this.error = null;
    this.conversationId = null;
    this.hasConversationStarted = true;
    this.creator = null;
    this.serviceStatusResult = null; // 重置服务状态结果
  }

  /**
   * 切换会话
   * 处理用户切换时的会话切换流程
   */
  async switchConversation() {
    try {
      logger.info('[ChatStore] 开始切换会话流程');

      // 获取当前选中的用户信息
      const { chatUserPickerStore } = await import('./ChatUserPickerStore');
      const selectedUserId = chatUserPickerStore.selectedUserId;
      const correctConversationId = chatUserPickerStore.correctConversationId;

      if (!selectedUserId || !correctConversationId) {
        logger.warn('[ChatStore] 缺少用户ID或会话ID，无法切换会话', {
          selectedUserId,
          correctConversationId,
        });
        return;
      }

      logger.info('[ChatStore] 切换会话参数', {
        selectedUserId,
        correctConversationId,
        virtualServiceUserId: chatUserPickerStore.correctVirtualServiceUserId,
      });

      // 用户切换时，重新请求服务状态数据，而不是使用缓存
      logger.info('[ChatStore] 用户切换，重新请求服务状态数据');

      // 获取当前用户信息
      const { accountStore } = await import('./AccountStore');
      const currentUser = accountStore.getCurrentUser();

      if (!currentUser) {
        logger.error('[ChatStore] 无法获取当前用户信息');
        throw new Error('无法获取当前用户信息');
      }

      // 重新请求服务状态数据
      const statusResult = await getServiceStatus({
        chatUserId: selectedUserId,
        tenantId: currentUser.tenantId,
      });

      if (!statusResult) {
        logger.error('[ChatStore] 获取服务状态失败');
        throw new Error('获取服务状态失败');
      }

      // 更新服务状态结果
      this.setServiceStatusResult(statusResult);

      // 同时更新 ChatUserPickerStore 的服务状态数据
      chatUserPickerStore.updateServiceStatusData(statusResult);

      // 更新用户信息到UserStore（包含会话ID）
      if (statusResult.buyerUser) {
        userStore._users.set(statusResult.buyerUser.id.toString(), {
          id: statusResult.buyerUser.id,
          nickName: statusResult.buyerUser.nickName,
          serialNumber: statusResult.buyerUser.serialNumber, // 添加序列号字段
          avatarUrl: statusResult.buyerUser.avatarUrl,
          currentGroupCount: statusResult.buyerUser.currentGroupCount,
          conversationId: statusResult.conversation.id, // 添加会话ID
        });
        logger.debug('[ChatStore] 切换会话时更新用户信息到UserStore:', {
          userId: statusResult.buyerUser.id,
          conversationId: statusResult.conversation.id,
          nickName: statusResult.buyerUser.nickName,
          serialNumber: statusResult.buyerUser.serialNumber, // 添加序列号日志
        });
      }

      // 验证服务状态结果
      if (!statusResult.virtualServiceUser?.id) {
        logger.error('[ChatStore] 服务状态结果中缺少虚拟客服ID');
        throw new Error('服务状态结果中缺少虚拟客服ID');
      }

      const serviceUserId = statusResult.virtualServiceUser.id;

      // 使用服务状态返回的conversationId，而不是chatUserPickerStore中的conversationId
      const serviceConversationId = statusResult.conversation?.id;
      if (!serviceConversationId) {
        logger.error('[ChatStore] 服务状态结果中缺少会话ID');
        throw new Error('服务状态结果中缺少会话ID');
      }

      logger.info('[ChatStore] 获取到虚拟客服ID', {
        serviceUserId,
        selectedUserId,
        originalConversationId: correctConversationId,
        serviceConversationId: serviceConversationId,
      });

      // 使用正确的虚拟客服ID和会话ID获取登录凭证
      const credentials = await this.getVirtualServiceCredentials({
        serviceUserId: serviceUserId,
        conversationId: serviceConversationId,
      });

      if (!credentials) {
        logger.error('[ChatStore] 获取登录凭证失败');
        throw new Error('获取登录凭证失败');
      }

      // 重要：更新登录凭证到ChatCredentialsStore，确保scriptLibrarySend等API使用正确的chatUserId
      // 优化：直接传递已获取的登录凭证，避免 LoginStateManager 重复调用登录接口
      await loginStateManager.handleLoginFlow({
        chatUserId: selectedUserId,
        tenantId: currentUser.tenantId,
        existingStatusResult: {
          conversation: {
            id: serviceConversationId,
          },
          virtualServiceUser: {
            id: serviceUserId,
          },
          buyerUser: {
            id: statusResult.buyerUser?.id,
          },
          needReLogin: false,
        },
        // 传递已获取的登录凭证，避免重复请求
        existingCredentials: credentials,
      });

      logger.info(
        '[ChatStore] 登录凭证已更新到ChatCredentialsStore，新的virtualServiceUser.id:',
        credentials.virtualServiceUser?.id
      );

      // 检查 iframe 是否已经就绪
      const iframe = document.querySelector('iframe');
      const isIframeReady = iframe && iframeCommunicationStore.isIframeReady;

      if (isIframeReady) {
        // iframe 已就绪，直接发送切换消息
        logger.info('[ChatStore] iframe 已就绪，直接发送切换消息');
        await this._sendSwitchMessageDirectly(credentials);
      } else {
        // iframe 未就绪，等待就绪后再发送
        logger.info('[ChatStore] iframe 未就绪，等待就绪后再发送切换消息');
        await this._waitForIframeReadyAndLogin(credentials);
      }

      logger.info('[ChatStore] 会话切换完成');
    } catch (error) {
      logger.error('[ChatStore] 切换会话失败:', error);
      throw error;
    }
  }

  async logout() {
    try {
      // 在新的架构中，凭证由子窗体管理，直接重置状态
      this.reset();
      return Promise.resolve(true);
    } catch (error) {
      console.warn('IM logout error:', error);
      this.reset();
      return Promise.reject(error);
    }
  }
}

export const chatStore = new ChatStore();
